import React, { useState, useMemo } from 'react';
import { DollarSign, Calendar, Percent, Coins } from 'lucide-react';
import CalculatorInput from '../shared/CalculatorInput';
import ResultCard from '../shared/ResultCard';
import Chart from '../shared/Chart';

const DividendCalculator: React.FC = () => {
  const [initialInvestment, setInitialInvestment] = useState(10000);
  const [monthlyInvestment, setMonthlyInvestment] = useState(500);
  const [dividendYield, setDividendYield] = useState(4);
  const [dividendGrowthRate, setDividendGrowthRate] = useState(5);
  const [years, setYears] = useState(20);
  const [reinvestDividends, setReinvestDividends] = useState(true);

  const calculatedData = useMemo(() => {
    const monthlyRate = dividendGrowthRate / 100 / 12;
    const months = years * 12;
    const data = [];

    let balance = initialInvestment;
    let totalContributed = initialInvestment;
    let totalDividends = 0;
    let currentYield = dividendYield;

    for (let i = 0; i <= months; i++) {
      if (i > 0) {
        balance += monthlyInvestment;
        totalContributed += monthlyInvestment;

        if (i % 3 === 0) {
          const quarterlyDividend = (balance * (currentYield / 100)) / 4;
          totalDividends += quarterlyDividend;
          
          if (reinvestDividends) {
            balance += quarterlyDividend;
          }
        }

        if (i % 12 === 0) {
          currentYield *= (1 + dividendGrowthRate / 100);
        }
      }

      if (i % 12 === 0) {
        data.push({
          year: i / 12,
          balance: Math.round(balance),
          contributed: Math.round(totalContributed),
          dividends: Math.round(totalDividends),
        });
      }
    }

    return data;
  }, [initialInvestment, monthlyInvestment, dividendYield, dividendGrowthRate, years, reinvestDividends]);

  const finalBalance = calculatedData[calculatedData.length - 1]?.balance || 0;
  const totalDividends = calculatedData[calculatedData.length - 1]?.dividends || 0;
  const annualDividendIncome = (finalBalance * (dividendYield / 100));

  const checkboxId = React.useId();

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden p-6 sm:p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Dividend Calculator</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <CalculatorInput
          label="Initial Investment"
          value={initialInvestment}
          onChange={setInitialInvestment}
          icon={<DollarSign size={20} />}
          prefix="$"
        />
        <CalculatorInput
          label="Monthly Investment"
          value={monthlyInvestment}
          onChange={setMonthlyInvestment}
          icon={<DollarSign size={20} />}
          prefix="$"
        />
        <CalculatorInput
          label="Current Dividend Yield"
          value={dividendYield}
          onChange={setDividendYield}
          icon={<Percent size={20} />}
          suffix="%"
        />
        <CalculatorInput
          label="Dividend Growth Rate"
          value={dividendGrowthRate}
          onChange={setDividendGrowthRate}
          icon={<Percent size={20} />}
          suffix="%"
        />
        <CalculatorInput
          label="Investment Period"
          value={years}
          onChange={setYears}
          icon={<Calendar size={20} />}
          suffix="years"
        />
      </div>

      <div className="mb-6">
        <div className="flex items-center space-x-2">
          <input
            id={checkboxId}
            type="checkbox"
            checked={reinvestDividends}
            onChange={(e) => setReinvestDividends(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          <label htmlFor={checkboxId} className="text-sm text-gray-700">
            Reinvest Dividends
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <ResultCard
          title="Portfolio Value"
          value={finalBalance}
          description="Total investment value"
          trend="up"
        />
        <ResultCard
          title="Total Dividends"
          value={totalDividends}
          description="Cumulative dividends received"
          trend="up"
        />
        <ResultCard
          title="Annual Dividend Income"
          value={annualDividendIncome}
          description="Current yearly dividend income"
          trend="up"
        />
      </div>

      <Chart data={calculatedData} />
    </div>
  );
};

export default DividendCalculator;